import type { AgendaItem, AgenteData, AgenteLog, DashboardData, Empresa, EmpresaKanban, KanbanData, KanbanStatus, MensagemPessoa, Pessoa, PessoaKanban, SettingsData } from '../types';
import { authService } from './authService';

const STORAGE_KEY = 'bim_crm_db_v2';
const DASHBOARD_URL =
  process.env.REACT_APP_N8N_AUTH_URL ||
  'https://apiwhattsdom-n8n.znwkmn.easypanel.host/webhook/78fd29a4-4c21-4abd-b45d-b0a49f27ca85';
const AGENTE_WEBHOOK_URL = 'https://apiwhattsdom-n8n.znwkmn.easypanel.host/webhook/78fd29a4-4c21-4abd-b45d-b0a49f27ca85';
const SAVE_EMPRESA_URL = 'https://apiwhattsdom-n8n.znwkmn.easypanel.host/webhook/78fd29a4-4c21-4abd-b45d-b0a49f27ca85';
const SEND_MESSAGE_URL = 'https://apiwhattsdom-n8n.znwkmn.easypanel.host/webhook/31acfea2-2b4b-4b2e-88a7-293aefc0fc4d';
const ENRICH_WHATTS_URL = 'https://apiwhattsdom-n8n.znwkmn.easypanel.host/webhook/78fd29a4-4c21-4abd-b45d-b0a49f27ca85';
const AGENDA_DEFAULT_TOKEN = '7d62cc1f-019f-4dab-9518-e8b1b2b37a97';

interface CRMDB {
  empresas: Empresa[];
  pessoas: Pessoa[];
  mensagens: MensagemPessoa[];
  agenda: AgendaItem[];
  settings: SettingsData;
  agente: AgenteData;
  agente_logs: AgenteLog[];
  prompt_apollo: string;
}

interface DashboardApiEmpresa {
  id?: string;
  nome_empresa?: string | null;
  telefone?: string | null;
  endereco?: string | null;
  website?: string | null;
  rating?: number | null;
  reviews?: number | null;
  email?: string | null;
  created_at?: string | null;
  disparo?: boolean | null;
}

interface DashboardApiEvento {
  iCalUID?: string;
  summary?: string | null;
  organizer?: {
    displayName?: string | null;
    email?: string | null;
  } | null;
  start?: {
    dateTime?: string | null;
    timeZone?: string | null;
  } | null;
  end?: {
    dateTime?: string | null;
    timeZone?: string | null;
  } | null;
}

interface DashboardApiResponse {
  success?: boolean;
  total_empresas?: number;
  empresas?: DashboardApiEmpresa[];
  eventos?: DashboardApiEvento[];
}

type DashboardApiRawResponse = DashboardApiResponse | DashboardApiResponse[];

interface GooglePesquisaApiResponse {
  id?: string;
  busca?: string | null;
  created_at?: string;
  user_id?: string;
  prompt_busca_apolo?: string | null;
}

type GooglePesquisaApiRawResponse = GooglePesquisaApiResponse | GooglePesquisaApiResponse[];

interface ApolloExtractApiResponse {
  success?: boolean;
  error?: string;
  message?: string;
}

type ApolloExtractApiRawResponse = ApolloExtractApiResponse | ApolloExtractApiResponse[];

interface AgenteWhattsApiResponse {
  id?: string;
  mensagem?: string | null;
  dispara_horario?: string | null;
  disparo_horario?: string | null;
  dispara_horario_fim?: string | null;
  disparo_horario_fim?: string | null;
  email_mensagem?: string | null;
}

type AgenteWhattsApiRawResponse = AgenteWhattsApiResponse | AgenteWhattsApiResponse[];

interface UserConfigApiResponse {
  instance?: string | null;
  api_key_serp?: string | null;
  api_key_apolo?: string | null;
  apikey?: string | null;
  google_calendarid?: string | null;
}

type UserConfigApiRawResponse = UserConfigApiResponse | UserConfigApiResponse[];

interface KanbanApiPessoa {
  pessoa_id?: string;
  empresa_id?: string;
  nome?: string | null;
  email?: string | null;
  telefone?: string | null;
  setor?: string | null;
  cargo?: string | null;
  foto_linkedin?: string | null;
  link_linkedin?: string | null;
  created_at?: string | null;
  status_email?: string | boolean | null;
  status_telefone?: string | boolean | null;
  sub_mensagens?: unknown[];
  mensagens_disparadas?: unknown[];
}

interface KanbanApiEmpresa {
  empresa_id?: string;
  nome_empresa?: string | null;
  telefone?: string | null;
  endereco?: string | null;
  website?: string | null;
  rating?: string | number | null;
  reviews?: number | null;
  email?: string | null;
  user_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  which_card?: 'captadas' | 'lista_qualificadas' | 'analise' | 'contato_iniciado' | 'contato_inciado' | 'retornaram' | 'cliente_retornou' | null;
  mensagens_disparadas_empresa?: unknown[];
  sub_mensagens_empresa?: unknown[];
  pessoas?: KanbanApiPessoa[];
}

interface KanbanApiResponse {
  total_empresas?: number;
  empresas?: KanbanApiEmpresa[];
}

type KanbanApiRawResponse = KanbanApiResponse | KanbanApiResponse[];

const nowISO = () => new Date().toISOString();

const unwrapFirstItem = <T>(payload: T | T[]): T | null => {
  if (Array.isArray(payload)) {
    const first = payload[0];
    return (first ?? null) as T | null;
  }

  return payload ?? null;
};

const cleanRemoteValue = (value?: string | null): string => {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  const lower = trimmed.toLowerCase();
  if (lower === 'undefined' || lower === 'null') return '';
  return trimmed;
};

const readStringPath = (source: unknown, path: string[]): string => {
  let current: unknown = source;
  for (const key of path) {
    if (!current || typeof current !== 'object') return '';
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === 'string' ? current : '';
};

const DEFAULT_AGENTE_WHATTS_PAYLOAD = {
  mensagem: 'Oi bom dia td bem? me chamo alex gerboni sou da empresa byte in motion ',
  dispara_horario: '17:30',
  dispara_horario_fim: '08:30',
  email_mensagem: 'sfddf'
};

const normalizeTimeHHMM = (value: string, fallback: string): string => {
  const raw = (value || '').trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return fallback;
  const hh = Number(match[1]);
  const mm = Number(match[2]);
  if (Number.isNaN(hh) || Number.isNaN(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) return fallback;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

const parseApiErrorText = async (response: Response): Promise<string> => {
  const raw = await response.text().catch(() => '');
  const text = raw.trim();
  if (!text) return '';
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    const msg = String(parsed.message || parsed.error || parsed.detail || '').trim();
    return msg || text;
  } catch {
    return text;
  }
};

const seedDB = (): CRMDB => ({
  empresas: [
    {
      id: 'e1',
      nome_empresa: 'Nova Engenharia',
      telefone: '+5511934567890',
      endereco: 'Av. Paulista, 1200 - São Paulo',
      website: 'https://nova-engenharia.com',
      rating: 4.7,
      reviews: 218,
      retornou: true,
      status_kanban: 'Retornaram',
      aberto: false
    },
    {
      id: 'e2',
      nome_empresa: 'Luso Digital',
      telefone: '+351912345678',
      endereco: 'Rua do Comércio, 45 - Lisboa',
      website: 'https://lusodigital.pt',
      rating: 4.2,
      reviews: 97,
      retornou: false,
      status_kanban: 'Contato iniciado',
      aberto: true
    },
    {
      id: 'e3',
      nome_empresa: 'Pulse Foods',
      telefone: '+5511987765432',
      endereco: 'Rua Augusta, 230 - São Paulo',
      website: 'https://pulsefoods.com.br',
      rating: 4.5,
      reviews: 153,
      retornou: true,
      status_kanban: 'Em análise',
      aberto: true
    }
  ],
  pessoas: [
    {
      id: 'p1',
      empresa_id: 'e1',
      setor: 'Engenharia',
      nome: 'Mariana Lopes',
      cargo: 'Diretora Comercial',
      foto_linkedin: 'https://i.pravatar.cc/120?img=14',
      email: 'mariana@nova-engenharia.com',
      telefone: '+5511991102233',
      status_email: true,
      status_telefone: true
    },
    {
      id: 'p2',
      empresa_id: 'e2',
      setor: 'Marketing',
      nome: 'João Vieira',
      cargo: 'Head de Growth',
      foto_linkedin: 'https://i.pravatar.cc/120?img=22',
      email: 'joao@lusodigital.pt',
      telefone: '+351934223344',
      status_email: false,
      status_telefone: true
    }
  ],
  mensagens: [
    { id: 'm1', pessoa_id: 'p1', canal: 'email', mensagem: 'Olá Mariana, podemos conversar amanhã?', direction: 'out', created_at: nowISO() },
    { id: 'm2', pessoa_id: 'p1', canal: 'email', mensagem: 'Claro, às 10h funciona.', direction: 'in', created_at: nowISO() },
    { id: 'm3', pessoa_id: 'p2', canal: 'telefone', mensagem: 'Follow-up da proposta enviada.', direction: 'out', created_at: nowISO() }
  ],
  agenda: [
    { id: 'a1', user_id: 'u_1', titulo: 'Call Nova Engenharia', data: '2026-02-25', hora: '10:00', descricao: 'Diagnóstico inicial' },
    { id: 'a2', user_id: 'u_1', titulo: 'Reunião Luso Digital', data: '2026-02-26', hora: '14:30', descricao: 'Validação de cenário atual' }
  ],
  settings: {
    api_key_serp: '',
    phone_agente: '+5511999999999',
    api_key_apolo: '',
    apikey: '',
    instance_name: 'BIM PROSPECT',
    google_calendarid: ''
  },
  agente: {
    ativo: false,
    schedule: { data: '2026-02-27', hora: '09', minuto: '30' },
    prompt_unico: 'Encontrar decisores B2B com alta intenção de compra',
    mensagem: 'Olá, vi que sua empresa pode se beneficiar da nossa solução.',
    categorias: ['Diretor Comercial'],
    email_template_html:
      '<p>Ola, tudo bem?</p><p>Vi que sua empresa pode ganhar escala comercial com nosso processo.</p><p>Podemos conversar esta semana?</p>',
    whatsapp_template:
      'Ola! Sou da Captacap. Vi que sua empresa tem perfil para aumentar geracao de oportunidades. Posso te explicar em 2 minutos?'
  },
  agente_logs: [{ id: 'l1', data: '2026-02-24 09:30', alvo: 'Nova Engenharia', status: 'sucesso' }],
  prompt_apollo: 'Buscar contatos de liderança comercial e growth'
});

const wait = (ms = 240) => new Promise((r) => setTimeout(r, ms));

const readDB = (): CRMDB => {
  if (typeof window === 'undefined') return seedDB();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const seeded = seedDB();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
      return seeded;
    }
    const parsed = JSON.parse(raw) as CRMDB;
    return parsed;
  } catch {
    const seeded = seedDB();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
    return seeded;
  }
};

const writeDB = (db: CRMDB) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
};

const brl = (n: number) =>
  `R$ ${n.toLocaleString('pt-BR', {
    maximumFractionDigits: 0
  })}`;

const emptyDashboard = (agenteOpen?: unknown): DashboardData => ({
  totalEmpresas: 0,
  retornaram: 0,
  abertos: 0,
  agenteAtivo: parseAgenteAtivo(agenteOpen),
  ultimasEmpresas: [],
  proximosCompromissos: []
});

const toLocalDashboard = (db: CRMDB, agenteOpen?: unknown): DashboardData => {
  const sortedEmpresas = [...db.empresas].sort((a, b) => b.id.localeCompare(a.id));
  const proximosCompromissos = [...db.agenda].sort((a, b) => `${a.data}${a.hora}`.localeCompare(`${b.data}${b.hora}`)).slice(0, 5);
  return {
    totalEmpresas: db.empresas.length,
    retornaram: db.empresas.filter((e) => e.retornou).length,
    abertos: db.empresas.filter((e) => e.aberto).length,
    agenteAtivo: typeof agenteOpen === 'undefined' ? db.agente.ativo : parseAgenteAtivo(agenteOpen),
    ultimasEmpresas: sortedEmpresas.slice(0, 6),
    proximosCompromissos
  };
};

const parseAgenteAtivo = (open: unknown): boolean => {
  if (typeof open === 'boolean') return open;
  if (typeof open === 'number') return open === 1;
  if (typeof open !== 'string') return false;

  const normalized = open.trim().toLowerCase();
  return ['1', 'true', 'ativo', 'active', 'on', 'sim', 'yes'].includes(normalized);
};

const mapEmpresaFromApi = (empresa: DashboardApiEmpresa, index: number): Empresa => {
  const email = empresa.email?.trim() || '';
  const nomeEmpresa = empresa.nome_empresa?.trim() || email || `Empresa ${index + 1}`;

  return {
    id: empresa.id || `empresa_${index + 1}`,
    nome_empresa: nomeEmpresa,
    telefone: empresa.telefone?.trim() || '-',
    endereco: empresa.endereco?.trim() || '-',
    website: empresa.website?.trim() || '-',
    rating: Number(empresa.rating ?? 0),
    reviews: Number(empresa.reviews ?? 0),
    retornou: false,
    status_kanban: 'Captadas',
    aberto: !(empresa.disparo ?? false)
  };
};

const parseISODateTime = (raw?: string | null): Date | null => {
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateFromISO = (raw: string, parsed: Date): string => {
  if (raw.length >= 10) return raw.slice(0, 10);
  return parsed.toISOString().slice(0, 10);
};

const formatTimeFromISO = (raw: string, parsed: Date): string => {
  if (raw.length >= 16) return raw.slice(11, 16);
  const hh = String(parsed.getHours()).padStart(2, '0');
  const mm = String(parsed.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const getOrganizerLabel = (evento: DashboardApiEvento): string =>
  evento.organizer?.displayName?.trim() || evento.organizer?.email?.trim() || 'Sem organizador';

const formatDateTimeLabel = (raw: string, parsed: Date): string => `${formatDateFromISO(raw, parsed)} ${formatTimeFromISO(raw, parsed)}`;

const parseEventosFromApi = (eventos: DashboardApiEvento[]): Array<{ timestamp: number; item: AgendaItem }> =>
  eventos
    .map((evento, index) => {
      const startDateTime = evento.start?.dateTime ?? null;
      const start = parseISODateTime(startDateTime);
      if (!startDateTime || !start) return null;

      const endDateTime = evento.end?.dateTime ?? null;
      const end = parseISODateTime(endDateTime);
      const organizer = getOrganizerLabel(evento);
      const endLabel = endDateTime && end ? formatDateTimeLabel(endDateTime, end) : 'Sem horário de término';

      return {
        timestamp: start.getTime(),
        item: {
          id: evento.iCalUID || `evento_${index + 1}`,
          user_id: '',
          titulo: evento.summary?.trim() || 'Compromisso',
          data: formatDateFromISO(startDateTime, start),
          hora: formatTimeFromISO(startDateTime, start),
          descricao: `Organizador: ${organizer} | Fim: ${endLabel}`
        } as AgendaItem
      };
    })
    .filter((item): item is { timestamp: number; item: AgendaItem } => item !== null)
    .sort((a, b) => a.timestamp - b.timestamp);

const mapEventosFromApi = (eventos: DashboardApiEvento[]): AgendaItem[] => {
  const parsed = parseEventosFromApi(eventos);
  const now = Date.now();
  const proximos = parsed.filter((p) => p.timestamp >= now).slice(0, 3);

  if (proximos.length > 0) return proximos.map((p) => p.item);
  return parsed.slice(-3).reverse().map((p) => p.item);
};

const mapEventosToAgendaFromApi = (eventos: DashboardApiEvento[]): AgendaItem[] =>
  parseEventosFromApi(eventos).map((entry) => entry.item);

const fetchDashboardFromApi = async (userId: string, agenteOpen: unknown): Promise<DashboardData> => {
  const response = await fetch(DASHBOARD_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      route: 'get_empresa',
      token: userId
    })
  });

  if (!response.ok) {
    throw new Error(`Falha ao buscar dashboard remoto: ${response.status}`);
  }

  const raw = (await response.json().catch(() => null)) as DashboardApiRawResponse | null;
  const payload = raw ? unwrapFirstItem(raw) : null;
  if (!payload) {
    return emptyDashboard(agenteOpen);
  }

  const empresas = [...(payload.empresas || [])]
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
    .slice(0, 3)
    .map(mapEmpresaFromApi);

  const proximosCompromissos = mapEventosFromApi(payload.eventos || []);

  return {
    totalEmpresas: payload.total_empresas ?? (payload.empresas?.length || 0),
    retornaram: 0,
    abertos: 0,
    agenteAtivo: parseAgenteAtivo(agenteOpen),
    ultimasEmpresas: empresas,
    proximosCompromissos
  };
};

const buildEmptyKanbanBoard = (): Record<KanbanStatus, EmpresaKanban[]> => ({
  Captadas: [],
  'Lista qualificada': [],
  'Em análise': [],
  'Contato iniciado': [],
  Retornaram: []
});

const emptyKanbanData = (): KanbanData => ({
  totalEmpresas: 0,
  board: buildEmptyKanbanBoard()
});

const mapWhichCardToStatus = (whichCard: KanbanApiEmpresa['which_card']): KanbanStatus => {
  if (whichCard === 'lista_qualificadas') return 'Lista qualificada';
  if (whichCard === 'captadas' || whichCard === 'analise') return 'Captadas';
  if (whichCard === 'contato_iniciado' || whichCard === 'contato_inciado') return 'Contato iniciado';
  if (whichCard === 'retornaram' || whichCard === 'cliente_retornou') return 'Retornaram';
  return 'Captadas';
};

const mapKanbanPessoa = (pessoa: KanbanApiPessoa, index: number, empresaId: string): PessoaKanban => ({
  id: pessoa.pessoa_id || `${empresaId}_pessoa_${index + 1}`,
  empresa_id: pessoa.empresa_id || empresaId,
  nome: pessoa.nome?.trim() || `Pessoa ${index + 1}`,
  email: pessoa.email ?? null,
  telefone: pessoa.telefone ?? null,
  setor: pessoa.setor ?? null,
  cargo: pessoa.cargo ?? null,
  foto_linkedin: pessoa.foto_linkedin ?? null,
  link_linkedin: pessoa.link_linkedin ?? null,
  created_at: pessoa.created_at ?? null,
  status_email: pessoa.status_email ?? null,
  status_telefone: pessoa.status_telefone ?? null,
  sub_mensagens: pessoa.sub_mensagens || [],
  mensagens_disparadas: pessoa.mensagens_disparadas || []
});

const mapKanbanEmpresa = (empresa: KanbanApiEmpresa, index: number): EmpresaKanban => {
  const id = empresa.empresa_id || `kanban_empresa_${index + 1}`;
  return {
    id,
    nome_empresa: empresa.nome_empresa?.trim() || `Empresa ${index + 1}`,
    telefone: empresa.telefone?.trim() || '-',
    endereco: empresa.endereco?.trim() || '-',
    website: empresa.website?.trim() || '-',
    rating: Number(empresa.rating ?? 0),
    reviews: Number(empresa.reviews ?? 0),
    email: empresa.email ?? null,
    user_id: empresa.user_id ?? null,
    created_at: empresa.created_at ?? null,
    updated_at: empresa.updated_at ?? null,
    which_card: empresa.which_card ?? null,
    mensagens_disparadas_empresa: empresa.mensagens_disparadas_empresa || [],
    sub_mensagens_empresa: empresa.sub_mensagens_empresa || [],
    pessoas: (empresa.pessoas || []).map((pessoa, pessoaIndex) => mapKanbanPessoa(pessoa, pessoaIndex, id))
  };
};

const toLocalKanbanData = (db: CRMDB): KanbanData => {
  const board = buildEmptyKanbanBoard();
  db.empresas.forEach((empresa) => {
    board[empresa.status_kanban].push({
      id: empresa.id,
      nome_empresa: empresa.nome_empresa,
      telefone: empresa.telefone,
      endereco: empresa.endereco,
      website: empresa.website,
      rating: empresa.rating,
      reviews: empresa.reviews,
      email: null,
      user_id: null,
      created_at: null,
      updated_at: null,
      which_card: null,
      mensagens_disparadas_empresa: [],
      sub_mensagens_empresa: [],
      pessoas: []
    });
  });

  return {
    totalEmpresas: db.empresas.length,
    board
  };
};

const fetchKanbanFromApi = async (userId: string): Promise<KanbanData> => {
  const response = await fetch(DASHBOARD_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      route: 'get_empresa_kanban',
      token: userId
    })
  });

  if (!response.ok) {
    throw new Error(`Falha ao buscar kanban remoto: ${response.status}`);
  }

  const raw = (await response.json().catch(() => null)) as KanbanApiRawResponse | null;
  const payload = raw ? unwrapFirstItem(raw) : null;
  if (!payload) {
    return emptyKanbanData();
  }

  const board = buildEmptyKanbanBoard();
  (payload.empresas || [])
    .map(mapKanbanEmpresa)
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
    .forEach((empresa) => {
      board[mapWhichCardToStatus(empresa.which_card)].push(empresa);
    });

  return {
    totalEmpresas: payload.total_empresas ?? (payload.empresas?.length || 0),
    board
  };
};

const statusToWhichCard = (status: KanbanStatus): NonNullable<KanbanApiEmpresa['which_card']> => {
  if (status === 'Captadas') return 'captadas';
  if (status === 'Lista qualificada') return 'lista_qualificadas';
  if (status === 'Contato iniciado') return 'contato_inciado';
  if (status === 'Retornaram') return 'retornaram';
  return 'captadas';
};

const updateKanbanCardRemote = async (userId: string, empresaId: string, status: KanbanStatus): Promise<void> => {
  const response = await fetch(DASHBOARD_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      route: 'update_empresa',
      token: userId,
      empresa_id: empresaId,
      which_card: statusToWhichCard(status)
    })
  });

  if (!response.ok) {
    throw new Error(`Falha ao atualizar card remoto: ${response.status}`);
  }

  const raw = await response.text().catch(() => '');
  if (!raw) return;

  try {
    const payload = JSON.parse(raw) as { success?: boolean } | null;
    if (payload && typeof payload.success === 'boolean' && payload.success !== true) {
      throw new Error('API nao confirmou a atualizacao do card.');
    }
  } catch {
    // Alguns webhooks retornam texto simples mesmo com HTTP 200; nesse caso consideramos sucesso.
    return;
  }
};

const sendMensagemRemote = async (
  token: string,
  payload: { pessoa_id: string; canal: 'email' | 'telefone'; mensagem: string; direction: 'out' | 'in' }
): Promise<void> => {
  const response = await fetch(SEND_MESSAGE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      token,
      id: token,
      ...payload
    })
  });

  if (!response.ok) {
    throw new Error(`Falha ao enviar mensagem remota: ${response.status}`);
  }
};

const deleteKanbanEmpresaRemote = async (token: string, userId: string, empresaId: string): Promise<void> => {
  const response = await fetch(DASHBOARD_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      route: 'delete_empresa',
      token,
      empresa_id: empresaId,
      user_id: userId
    })
  });

  if (!response.ok) {
    throw new Error(`Falha ao excluir empresa remota: ${response.status}`);
  }
};

const fetchGooglePesquisaPayloadFromApi = async (token: string): Promise<GooglePesquisaApiResponse | null> => {
  const response = await fetch(DASHBOARD_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      route: 'get_pesquisa',
      token
    })
  });

  if (!response.ok) {
    throw new Error(`Falha ao buscar pesquisa remota: ${response.status}`);
  }

  const raw = (await response.json().catch(() => null)) as GooglePesquisaApiRawResponse | null;
  const payload = raw ? unwrapFirstItem(raw) : null;
  if (!payload) return null;
  return payload;
};

const fetchGooglePesquisaFromApi = async (token: string): Promise<string> => {
  const payload = await fetchGooglePesquisaPayloadFromApi(token);
  if (!payload) return '';
  return payload.busca?.trim() || '';
};

const updateGooglePesquisaFromApi = async (token: string, busca: string, prompt = ''): Promise<void> => {
  const response = await fetch(DASHBOARD_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      route: 'update_prompt_pesquisa',
      token,
      prompt,
      busca
    })
  });

  if (!response.ok) {
    throw new Error(`Falha ao salvar pesquisa remota: ${response.status}`);
  }
};

const runApolloExtractionFromApi = async (token: string, prompt: string, busca: string): Promise<void> => {
  const response = await fetch(DASHBOARD_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      route: 'extract_apolo',
      token,
      prompt,
      busca
    })
  });

  if (!response.ok) {
    throw new Error(`Falha ao executar extracao Apollo: ${response.status}`);
  }

  const raw = (await response.json().catch(() => null)) as ApolloExtractApiRawResponse | null;
  const payload = raw ? unwrapFirstItem(raw) : null;
  if (!payload) return;

  if (payload.success === false) {
    throw new Error(payload.error || payload.message || 'Falha na extracao Apollo.');
  }
};

const runEnrichWhattsFromApi = async (token: string, nome: string, prompt = '', busca = 'Busque hotel em são paulo'): Promise<void> => {
  const response = await fetch(ENRICH_WHATTS_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      route: 'enriquecer_whatts',
      token,
      data: [{ nome }],
      prompt,
      busca
    })
  });

  if (!response.ok) {
    const details = await parseApiErrorText(response);
    throw new Error(`Falha ao enriquecer empresa: ${response.status}${details ? ` - ${details}` : ''}`);
  }

  const raw = await response.json().catch(() => null);
  if (!Array.isArray(raw)) {
    throw new Error('Resposta invalida no enriquecimento.');
  }

  const first = raw[0];
  if (!first || typeof first !== 'object') {
    throw new Error('Resposta invalida no enriquecimento.');
  }
};

const fetchAgenteWhattsFromApi = async (token: string): Promise<AgenteWhattsApiResponse | null> => {
  const response = await fetch(AGENTE_WEBHOOK_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      route: 'get_agente_whatts',
      token
    })
  });

  if (!response.ok) {
    throw new Error(`Falha ao buscar agente whatts: ${response.status}`);
  }

  const raw = (await response.json().catch(() => null)) as unknown;
  if (!raw) return null;

  const candidates: unknown[] = [raw];
  if (Array.isArray(raw) && raw.length) candidates.push(raw[0]);
  if (typeof raw === 'object' && raw !== null) {
    const asRecord = raw as Record<string, unknown>;
    candidates.push(asRecord.data, asRecord.result, asRecord.agente_whatts, asRecord.agente);
    if (Array.isArray(asRecord.data) && asRecord.data.length) candidates.push(asRecord.data[0]);
    if (Array.isArray(asRecord.result) && asRecord.result.length) candidates.push(asRecord.result[0]);
  }

  for (const item of candidates) {
    if (!item || typeof item !== 'object') continue;
    const id =
      readStringPath(item, ['id']) ||
      readStringPath(item, ['agente_id']) ||
      readStringPath(item, ['uuid']) ||
      readStringPath(item, ['data', 'id']);
    const mensagem = readStringPath(item, ['mensagem']) || readStringPath(item, ['message']);
    const dispara_horario =
      readStringPath(item, ['dispara_horario']) ||
      readStringPath(item, ['disparo_horario']) ||
      readStringPath(item, ['disparaHorario']) ||
      readStringPath(item, ['hora_inicio']) ||
      readStringPath(item, ['start_time']);
    const dispara_horario_fim =
      readStringPath(item, ['dispara_horario_fim']) ||
      readStringPath(item, ['disparo_horario_fim']) ||
      readStringPath(item, ['disparaHorarioFim']) ||
      readStringPath(item, ['hora_fim']) ||
      readStringPath(item, ['end_time']);
    const email_mensagem = readStringPath(item, ['email_mensagem']) || readStringPath(item, ['emailMensagem']) || readStringPath(item, ['email']);

    const hasAnyValue = [id, mensagem, dispara_horario, dispara_horario_fim, email_mensagem].some((value) => value.trim().length > 0);
    if (!hasAnyValue) continue;

    return {
      id: id || undefined,
      mensagem: mensagem || undefined,
      dispara_horario: dispara_horario || undefined,
      dispara_horario_fim: dispara_horario_fim || undefined,
      email_mensagem: email_mensagem || undefined
    };
  }

  return null;
};

const createAgenteWhattsInApi = async (token: string, payload: { mensagem: string; dispara_horario: string; dispara_horario_fim: string; email_mensagem: string }): Promise<void> => {
  const response = await fetch(AGENTE_WEBHOOK_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      route: 'create_agente_whatts',
      token,
      ...payload
    })
  });

  if (!response.ok) {
    const details = await parseApiErrorText(response);
    throw new Error(`Falha ao criar agente whatts: ${response.status}${details ? ` - ${details}` : ''}`);
  }
};

const updateAgenteWhattsInApi = async (
  token: string,
  payload: { id: string; mensagem: string; dispara_horario: string; dispara_horario_fim: string; email_mensagem: string }
): Promise<void> => {
  const response = await fetch(AGENTE_WEBHOOK_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      route: 'update_agente_whatts',
      token,
      ...payload
    })
  });

  if (!response.ok) {
    const details = await parseApiErrorText(response);
    throw new Error(`Falha ao atualizar agente whatts: ${response.status}${details ? ` - ${details}` : ''}`);
  }
};

const toAgenteWhattsPayload = (data: AgenteData) => {
  const mensagem = (data.whatsapp_template || data.mensagem || '').trim();
  const disparaHorario = normalizeTimeHHMM(data.schedule.hora.trim(), DEFAULT_AGENTE_WHATTS_PAYLOAD.dispara_horario);
  const disparaHorarioFim = normalizeTimeHHMM(data.schedule.minuto.trim(), DEFAULT_AGENTE_WHATTS_PAYLOAD.dispara_horario_fim);
  const emailMensagem = (data.email_template_html || '').trim();

  return {
    mensagem,
    dispara_horario: disparaHorario,
    dispara_horario_fim: disparaHorarioFim,
    email_mensagem: emailMensagem
  };
};

const updateAgenteOpenInApi = async (token: string, open: boolean): Promise<void> => {
  const response = await fetch(AGENTE_WEBHOOK_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      route: 'update_ativar_agente',
      token,
      open
    })
  });

  if (!response.ok) {
    throw new Error(`Falha ao atualizar status do agente: ${response.status}`);
  }
};

const fetchUserConfigFromApi = async (token: string): Promise<SettingsData> => {
  const response = await fetch(DASHBOARD_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      route: 'get_users',
      token
    })
  });

  if (!response.ok) {
    throw new Error(`Falha ao buscar configuracoes remotas: ${response.status}`);
  }

  const raw = (await response.json().catch(() => null)) as UserConfigApiRawResponse | null;
  const payload = raw ? unwrapFirstItem(raw) : null;
  if (!payload) {
    throw new Error('Resposta inválida ao buscar configurações.');
  }

  return {
    api_key_serp: cleanRemoteValue(payload.api_key_serp),
    phone_agente: '',
    api_key_apolo: cleanRemoteValue(payload.api_key_apolo),
    apikey: cleanRemoteValue(payload.apikey),
    instance_name: cleanRemoteValue(payload.instance),
    google_calendarid: cleanRemoteValue(payload.google_calendarid)
  };
};

const updateUserConfigInApi = async (token: string, settings: SettingsData): Promise<void> => {
  const response = await fetch(DASHBOARD_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      route: 'update_configuracao',
      token,
      api_key_serp: settings.api_key_serp.trim(),
      api_key_apolo: settings.api_key_apolo.trim(),
      apikey: settings.apikey.trim(),
      instance: settings.instance_name.trim(),
      google_calendarid: settings.google_calendarid.trim()
    })
  });

  if (!response.ok) {
    throw new Error(`Falha ao salvar configuracoes remotas: ${response.status}`);
  }

  // Alguns fluxos não persistem google_calendarid em update_configuracao.
  // Garantimos a gravação com update_perfil usando o mesmo token do usuário.
  const profileResponse = await fetch(DASHBOARD_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      route: 'update_perfil',
      token,
      google_calendarid: settings.google_calendarid.trim()
    })
  });

  if (!profileResponse.ok) {
    throw new Error(`Falha ao salvar Google Agenda remoto: ${profileResponse.status}`);
  }
};

export const crmService = {
  async getDashboard(): Promise<DashboardData> {
    await wait();
    const session = authService.getSession();
    const userId = session?.user?.id?.trim();

    if (!userId) return emptyDashboard(session?.user?.open);

    try {
      return await fetchDashboardFromApi(userId, session?.user?.open);
    } catch {
      return emptyDashboard(session?.user?.open);
    }
  },

  async listEmpresas(params?: { search?: string; status?: KanbanStatus | 'all' }): Promise<Empresa[]> {
    await wait();
    const db = readDB();
    return db.empresas.filter((item) => {
      const searchOk =
        !params?.search ||
        `${item.nome_empresa} ${item.website} ${item.telefone}`.toLowerCase().includes(params.search.toLowerCase());
      const statusOk = !params?.status || params.status === 'all' || item.status_kanban === params.status;
      return searchOk && statusOk;
    });
  },

  async getEmpresaById(id: string): Promise<Empresa | null> {
    await wait();
    return readDB().empresas.find((e) => e.id === id) || null;
  },

  async setKanbanStatus(id: string, status: KanbanStatus): Promise<void> {
    await wait();
    const db = readDB();
    db.empresas = db.empresas.map((e) => (e.id === id ? { ...e, status_kanban: status, retornou: status === 'Retornaram', aberto: status !== 'Retornaram' } : e));
    writeDB(db);
  },

  async getPessoasByEmpresa(empresaId: string): Promise<Pessoa[]> {
    await wait();
    return readDB().pessoas.filter((p) => p.empresa_id === empresaId);
  },

  async getMensagensPessoa(pessoaId: string): Promise<MensagemPessoa[]> {
    await wait();
    return readDB().mensagens.filter((m) => m.pessoa_id === pessoaId);
  },

  async sendMensagem(pessoaId: string, canal: 'email' | 'telefone', mensagem: string, direction: 'out' | 'in'): Promise<void> {
    await wait(120);
    const session = authService.getSession();
    const token = session?.user?.id?.trim() || '';

    if (token) {
      await sendMensagemRemote(token, {
        pessoa_id: pessoaId,
        canal,
        mensagem,
        direction
      });
    }

    const db = readDB();
    db.mensagens.push({
      id: `m_${Date.now()}`,
      pessoa_id: pessoaId,
      canal,
      mensagem,
      direction,
      created_at: nowISO()
    });
    writeDB(db);
  },

  async runGoogleSearch(query: string): Promise<Empresa[]> {
    await wait(900);
    const db = readDB();
    const generated: Empresa[] = Array.from({ length: 5 }).map((_, idx) => ({
      id: `e_${Date.now()}_${idx}`,
      nome_empresa: `${query} ${idx + 1}`,
      telefone: `+55 11 9${Math.floor(10000000 + Math.random() * 89999999)}`,
      endereco: `Rua Exemplo ${idx + 10}, São Paulo`,
      website: `https://${query.toLowerCase().replace(/\s+/g, '')}${idx + 1}.com`,
      rating: Number((3.8 + Math.random() * 1.1).toFixed(1)),
      reviews: Math.floor(20 + Math.random() * 220),
      retornou: false,
      status_kanban: 'Captadas',
      aberto: true
    }));
    db.empresas.unshift(...generated);
    writeDB(db);
    return generated;
  },

  async getGoogleSearchQuery(): Promise<string> {
    await wait();
    const session = authService.getSession();
    const token = session?.user?.id?.trim() || AGENDA_DEFAULT_TOKEN;

    if (!token) return '';

    try {
      return await fetchGooglePesquisaFromApi(token);
    } catch {
      return '';
    }
  },

  async saveGoogleSearchQuery(busca: string, prompt = ''): Promise<void> {
    await wait();
    const session = authService.getSession();
    const token = session?.user?.id?.trim() || AGENDA_DEFAULT_TOKEN;
    const sanitizedBusca = busca.trim();

    if (!token || !sanitizedBusca) return;

    await updateGooglePesquisaFromApi(token, sanitizedBusca, prompt);
  },

  async getKanbanData(): Promise<KanbanData> {
    await wait();
    const session = authService.getSession();
    const userId = session?.user?.id?.trim();

    if (!userId) return emptyKanbanData();

    try {
      return await fetchKanbanFromApi(userId);
    } catch {
      return emptyKanbanData();
    }
  },

  async updateEmpresaKanban(empresaId: string, status: KanbanStatus): Promise<void> {
    const session = authService.getSession();
    const userId = session?.user?.id?.trim();

    if (userId) {
      await updateKanbanCardRemote(userId, empresaId, status);
      return;
    }

    await this.setKanbanStatus(empresaId, status);
  },

  async enrichEmpresaKanban(nomeEmpresa: string, busca = 'Busque hotel em são paulo', prompt = ''): Promise<void> {
    const nome = nomeEmpresa.trim();
    if (!nome) {
      throw new Error('Nome da empresa invalido para enriquecimento.');
    }

    const session = authService.getSession();
    const token = session?.user?.id?.trim() || AGENDA_DEFAULT_TOKEN;

    if (!token) {
      throw new Error('Sessao invalida. Faca login novamente.');
    }

    await runEnrichWhattsFromApi(token, nome, prompt, busca);
  },

  async deleteEmpresaKanban(empresaId: string): Promise<void> {
    await wait();
    const session = authService.getSession();
    const sessionUserId = session?.user?.id?.trim() || '';
    const token = sessionUserId || AGENDA_DEFAULT_TOKEN;

    if (sessionUserId) {
      await deleteKanbanEmpresaRemote(token, sessionUserId, empresaId);
    }

    const db = readDB();
    db.empresas = db.empresas.filter((empresa) => empresa.id !== empresaId);
    db.pessoas = db.pessoas.filter((pessoa) => pessoa.empresa_id !== empresaId);
    writeDB(db);
  },

  async getKanban(): Promise<Record<KanbanStatus, Empresa[]>> {
    await wait();
    const db = readDB();
    const col: Record<KanbanStatus, Empresa[]> = {
      Captadas: [],
      'Lista qualificada': [],
      'Em análise': [],
      'Contato iniciado': [],
      Retornaram: []
    };
    db.empresas.forEach((e) => col[e.status_kanban].push(e));
    return col;
  },

  async getAgenda(): Promise<AgendaItem[]> {
    await wait();
    const session = authService.getSession();
    const token = session?.user?.id?.trim() || AGENDA_DEFAULT_TOKEN;

    if (!token) return [];

    try {
      const response = await fetch(DASHBOARD_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          route: 'get_empresa',
          token
        })
      });

      if (!response.ok) {
        throw new Error(`Falha ao buscar agenda remota: ${response.status}`);
      }

      const raw = (await response.json().catch(() => null)) as DashboardApiRawResponse | null;
      const payload = raw ? unwrapFirstItem(raw) : null;
      if (!payload) return [];

      const remoteAgenda = mapEventosToAgendaFromApi(payload.eventos || []);
      return remoteAgenda;
    } catch {
      return [];
    }
  },

  async createAgenda(item: Omit<AgendaItem, 'id'>): Promise<void> {
    await wait();
    const db = readDB();
    db.agenda.push({ ...item, id: `a_${Date.now()}` });
    writeDB(db);
  },

  async deleteAgenda(id: string): Promise<void> {
    await wait();
    const db = readDB();
    db.agenda = db.agenda.filter((a) => a.id !== id);
    writeDB(db);
  },

  async getAbertos(): Promise<Array<Empresa | Pessoa>> {
    await wait();
    const db = readDB();
    const empresasAbertas = db.empresas.filter((e) => e.aberto);
    const pessoasPendentes = db.pessoas.filter((p) => !p.status_email || !p.status_telefone);
    return [...empresasAbertas, ...pessoasPendentes];
  },

  async getSettings(): Promise<SettingsData> {
    await wait();
    const fallback = readDB().settings;
    const session = authService.getSession();
    const token = session?.user?.id?.trim() || AGENDA_DEFAULT_TOKEN;

    if (!token) return fallback;

    try {
      const remote = await fetchUserConfigFromApi(token);
      const db = readDB();
      db.settings = {
        ...db.settings,
        api_key_serp: remote.api_key_serp,
        api_key_apolo: remote.api_key_apolo,
        apikey: remote.apikey,
        instance_name: remote.instance_name,
        google_calendarid: remote.google_calendarid
      };
      writeDB(db);
      return db.settings;
    } catch {
      return fallback;
    }
  },

  async updateSettings(settings: SettingsData): Promise<void> {
    await wait();
    const session = authService.getSession();
    const token = session?.user?.id?.trim() || AGENDA_DEFAULT_TOKEN;

    if (token) {
      await updateUserConfigInApi(token, settings);
    }

    const db = readDB();
    db.settings = settings;
    writeDB(db);
  },

  async getAgente(): Promise<AgenteData> {
    await wait();
    const db = readDB();
    const agente = db.agente;
    const session = authService.getSession();
    const ativoFromSession = session ? parseAgenteAtivo(session.user?.open) : agente.ativo;

    const fallback: AgenteData = {
      ...agente,
      ativo: ativoFromSession,
      email_template_html:
        agente.email_template_html ||
        '<p>Ola, tudo bem?</p><p>Vi que sua empresa pode ganhar escala comercial com nosso processo.</p><p>Podemos conversar esta semana?</p>',
      whatsapp_template:
        agente.whatsapp_template ||
        agente.mensagem ||
        'Ola! Sou da Captacap. Vi que sua empresa tem perfil para aumentar geracao de oportunidades. Posso te explicar em 2 minutos?'
    };

    const token = session?.user?.id?.trim();
    if (!token) return fallback;

    try {
      let remoteAgente = await fetchAgenteWhattsFromApi(token);
      if (!remoteAgente || !remoteAgente.id?.trim()) {
        await createAgenteWhattsInApi(token, DEFAULT_AGENTE_WHATTS_PAYLOAD);
        remoteAgente = await fetchAgenteWhattsFromApi(token);
      }

      if (!remoteAgente) {
        return {
          ...fallback,
          schedule: {
            ...fallback.schedule,
            hora: DEFAULT_AGENTE_WHATTS_PAYLOAD.dispara_horario,
            minuto: DEFAULT_AGENTE_WHATTS_PAYLOAD.dispara_horario_fim
          },
          mensagem: DEFAULT_AGENTE_WHATTS_PAYLOAD.mensagem,
          whatsapp_template: DEFAULT_AGENTE_WHATTS_PAYLOAD.mensagem,
          email_template_html: DEFAULT_AGENTE_WHATTS_PAYLOAD.email_mensagem
        };
      }

      const mapped: AgenteData = {
        ...fallback,
        id: remoteAgente.id || fallback.id,
        schedule: {
          ...fallback.schedule,
          hora: normalizeTimeHHMM(remoteAgente.dispara_horario?.trim() || remoteAgente.disparo_horario?.trim() || '', fallback.schedule.hora || DEFAULT_AGENTE_WHATTS_PAYLOAD.dispara_horario),
          minuto: normalizeTimeHHMM(remoteAgente.dispara_horario_fim?.trim() || remoteAgente.disparo_horario_fim?.trim() || '', fallback.schedule.minuto || DEFAULT_AGENTE_WHATTS_PAYLOAD.dispara_horario_fim)
        },
        mensagem: remoteAgente.mensagem?.trim() || fallback.mensagem,
        whatsapp_template: remoteAgente.mensagem?.trim() || fallback.whatsapp_template,
        email_template_html: remoteAgente.email_mensagem?.trim() || fallback.email_template_html
      };

      db.agente = {
        ...db.agente,
        ...mapped,
        ativo: mapped.ativo
      };
      writeDB(db);

      return mapped;
    } catch {
      return fallback;
    }
  },

  async updateAgente(data: AgenteData): Promise<void> {
    await wait();
    const db = readDB();
    const session = authService.getSession();
    const ativoFromSession = session ? parseAgenteAtivo(session.user?.open) : data.ativo;
    const nextLocal: AgenteData = {
      ...data,
      ativo: ativoFromSession,
      mensagem: data.whatsapp_template || data.mensagem
    };

    db.agente = nextLocal;
    writeDB(db);

    const token = session?.user?.id?.trim();
    if (!token) return;

    const payload = toAgenteWhattsPayload(data);

    const currentId = data.id?.trim();
    if (currentId) {
      await updateAgenteWhattsInApi(token, {
        id: currentId,
        ...payload
      });
      return;
    }

    const existing = await fetchAgenteWhattsFromApi(token);
    if (!existing?.id?.trim()) {
      await createAgenteWhattsInApi(token, payload);
      return;
    }

    await updateAgenteWhattsInApi(token, {
      id: existing.id,
      ...payload
    });
  },

  async upsertAgenteWhatts(data: AgenteData): Promise<AgenteData> {
    await wait();
    const session = authService.getSession();
    const token = session?.user?.id?.trim();
    if (!token) throw new Error('Sessao invalida para salvar agente.');

    const payload = toAgenteWhattsPayload(data);
    const existing = await fetchAgenteWhattsFromApi(token);
    const existingId = existing?.id?.trim();
    const currentId = data.id?.trim();
    const idToUpdate = existingId || currentId;

    if (idToUpdate) {
      await updateAgenteWhattsInApi(token, {
        id: idToUpdate,
        ...payload
      });
    } else {
      await createAgenteWhattsInApi(token, payload);
      const afterCreate = await fetchAgenteWhattsFromApi(token);
      if (afterCreate?.id?.trim()) {
        await updateAgenteWhattsInApi(token, {
          id: afterCreate.id,
          ...payload
        });
      }
    }

    const refreshed = await this.getAgente();
    if (!refreshed.id?.trim()) {
      throw new Error('Agente salvo, mas id nao foi retornado pelo get_agente_whatts.');
    }
    return refreshed;
  },

  async setAgenteActive(open: boolean): Promise<void> {
    await wait();
    const session = authService.getSession();
    const token = session?.user?.id?.trim();
    if (!token) return;

    await updateAgenteOpenInApi(token, open);

    if (session) {
      const nextSession = {
        ...session,
        user: {
          ...session.user,
          open
        }
      };
      authService.saveSession(nextSession);
    }

    const db = readDB();
    db.agente = {
      ...db.agente,
      ativo: open
    };
    writeDB(db);
  },

  async getAgenteLogs(): Promise<AgenteLog[]> {
    await wait();
    return [...readDB().agente_logs].reverse();
  },

  async runAgente(target = 'Execução manual'): Promise<void> {
    await wait(500);
    const db = readDB();
    const ok = Math.random() > 0.15;
    db.agente_logs.push({
      id: `l_${Date.now()}`,
      data: new Date().toLocaleString('pt-BR'),
      alvo: target,
      status: ok ? 'sucesso' : 'erro'
    });
    writeDB(db);
  },

  async getPromptApollo(): Promise<string> {
    await wait();
    const session = authService.getSession();
    const token = session?.user?.id?.trim() || AGENDA_DEFAULT_TOKEN;

    if (!token) return '';

    try {
      const payload = await fetchGooglePesquisaPayloadFromApi(token);
      if (!payload) return '';

      return payload.prompt_busca_apolo?.trim() || payload.busca?.trim() || '';
    } catch {
      return '';
    }
  },

  async savePromptApollo(prompt: string): Promise<void> {
    await wait();
    const session = authService.getSession();
    const token = session?.user?.id?.trim() || AGENDA_DEFAULT_TOKEN;
    const sanitizedPrompt = prompt.trim();

    if (!token || !sanitizedPrompt) return;

    await updateGooglePesquisaFromApi(token, sanitizedPrompt, sanitizedPrompt);
  },

  async runApollo(prompt: string, busca: string): Promise<void> {
    await wait(700);
    const session = authService.getSession();
    const token = session?.user?.id?.trim() || AGENDA_DEFAULT_TOKEN;
    const sanitizedPrompt = prompt.trim();
    const sanitizedBusca = busca.trim();

    if (!token || !sanitizedPrompt || !sanitizedBusca) {
      throw new Error('Prompt e busca sao obrigatorios para executar o Apollo.');
    }

    await runApolloExtractionFromApi(token, sanitizedPrompt, sanitizedBusca);
  },

  async getApolloSearchConfig(): Promise<{ prompt: string; busca: string }> {
    await wait();
    const session = authService.getSession();
    const token = session?.user?.id?.trim() || AGENDA_DEFAULT_TOKEN;

    if (!token) return { prompt: '', busca: '' };

    try {
      const payload = await fetchGooglePesquisaPayloadFromApi(token);
      if (!payload) return { prompt: '', busca: '' };

      const busca = payload.busca?.trim() || '';
      const prompt = payload.prompt_busca_apolo?.trim() || busca;
      return { prompt, busca };
    } catch {
      return { prompt: '', busca: '' };
    }
  },

  estimatePotential(e: Empresa) {
    return brl(e.reviews * e.rating * 120);
  }
};
